import { useLocalStorage } from '@vueuse/core';
import type { Ref } from 'vue';
import type { MeasurementUnit } from '../domain/units';
import type { WeightUnit } from '../domain/weightUnits';

const cache = new Map<string, Ref<MeasurementUnit>>();

/**
 * Returns a persisted, reactive measurement unit for a field. Instances that
 * pass the same `unitKey` share the exact same ref (module-level memoized), so
 * an input and, say, a table column bound to the same key stay in sync and
 * persist the user's choice in localStorage. Defaults to meters.
 */
export function useFieldUnit(unitKey: string): Ref<MeasurementUnit> {
  let unit = cache.get(unitKey);
  if (!unit) {
    unit = useLocalStorage<MeasurementUnit>(`measurement-unit:${unitKey}`, 'm');
    cache.set(unitKey, unit);
  }
  return unit;
}

const weightCache = new Map<string, Ref<WeightUnit>>();

/**
 * Weight counterpart of {@link useFieldUnit}. Memoized per `unitKey` so an
 * input and a table column sharing the key stay in sync. Defaults to kilograms.
 */
export function useWeightUnit(unitKey: string): Ref<WeightUnit> {
  let unit = weightCache.get(unitKey);
  if (!unit) {
    unit = useLocalStorage<WeightUnit>(`weight-unit:${unitKey}`, 'kg');
    weightCache.set(unitKey, unit);
  }
  return unit;
}
